import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "card-hdr" }
const _hoisted_2 = { class: "card-title" }
const _hoisted_3 = {
  key: 0,
  class: "current-stats"
}
const _hoisted_4 = {
  key: 0,
  class: "stat-item temp"
}
const _hoisted_5 = {
  key: 1,
  class: "stat-item hum"
}
const _hoisted_6 = {
  key: 2,
  class: "stat-last-update"
}
const _hoisted_7 = { class: "card-tools" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "toolbar-limits" }
const _hoisted_10 = {
  key: 0,
  class: "limit-group"
}
const _hoisted_11 = {
  key: 1,
  class: "limit-group"
}
const _hoisted_12 = {
  key: 0,
  class: "loading-overlay"
}

import { computed, ref, watch } from "vue";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale,
  Filler,
} from "chart.js";
import { Line } from "vue-chartjs";
import "chartjs-adapter-luxon";
import zoomPlugin from "chartjs-plugin-zoom";
import { chartTimeConfig } from "@/utils/chartTime";

// Registra i componenti necessari
import { faSliders } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

/* Formatting Helpers */

export default /*@__PURE__*/_defineComponent({
  __name: 'THChartCard',
  props: {
    title: {},
    type: {},
    seriesT: {},
    seriesH: {},
    currentRange: {},
    currentTemp: {},
    currentHum: {},
    lastUpdateAgo: {},
    tempMin: {},
    tempMax: {},
    humMin: {},
    humMax: {},
    loading: { type: Boolean }
  },
  emits: ["update:range", "refresh", "config"],
  setup(__props: any) {

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale,
  Filler,
  zoomPlugin
);

const props = __props;

const formatTemp = (v: number) => `${v.toFixed(1)}°C`;
const formatHum = (v: number) => `${Math.round(v)}%`;



const chartRef = ref<any>(null);
const showZoomHint = ref(false);
let hintTimeout: any = null;

function handleWheel(e: WheelEvent) {
  if (!e.altKey) {
    showZoomHint.value = true;
    if (hintTimeout) clearTimeout(hintTimeout);
    hintTimeout = setTimeout(() => {
      showZoomHint.value = false;
    }, 2000);
  } else {
    showZoomHint.value = false;
  }
}

function resetZoom() {
  if (chartRef.value?.chart) {
    chartRef.value.chart.resetZoom();
  }
}

function zoomIn() {
  if (chartRef.value?.chart) {
    chartRef.value.chart.zoom(1.2);
  }
}

function zoomOut() {
  if (chartRef.value?.chart) {
    chartRef.value.chart.zoom(0.8);
  }
}

// Watch per il cambio range nel componente TH (Temperature/Humidity)
watch(() => props.currentRange, (newRange: string) => {
  if (chartRef.value?.chart) {
    updateChartIntervals(chartRef.value.chart, newRange);
  }
});

function updateChartIntervals(chart: any, forcedRange: string | null = null) {
  if (!chart) return;
  
  let rangeHours = 24; // Default fallback

  if (forcedRange) {
    if (forcedRange === "1h") rangeHours = 1;
    else if (forcedRange === "24h") rangeHours = 24;
    else if (forcedRange === "7d") rangeHours = 168;
    else if (forcedRange === "30d") rangeHours = 720;
  } else {
    const scaleX = chart.scales.x;
    if (scaleX && scaleX.min !== undefined && scaleX.max !== undefined) {
      const rangeMs = scaleX.max - scaleX.min;
      rangeHours = rangeMs / (1000 * 60 * 60);
    }
  }

  let unit = "minute" as const;
  let stepSize = 30;

  if (rangeHours <= 1.1) {
    stepSize = 1;
  } else if (rangeHours <= 4.1) {
    stepSize = 10;
  } else if (rangeHours <= 8.1) {
    stepSize = 15;
  } else {
    stepSize = 30;
  }

  if (chart.options.scales.x.time && chart.options.scales.x.ticks) {
    chart.options.scales.x.time.unit = unit;
    chart.options.scales.x.time.stepSize = stepSize;
    chart.options.scales.x.bounds = 'ticks';
    if (rangeHours <= 25.1) {
      chart.options.scales.x.ticks.autoSkip = false;
      chart.options.scales.x.ticks.maxRotation = 45;
      chart.options.scales.x.ticks.minRotation = 45;
      chart.options.scales.x.ticks.maxTicksLimit = 100;
    } else {
      chart.options.scales.x.ticks.autoSkip = true;
      chart.options.scales.x.ticks.maxRotation = 0;
      chart.options.scales.x.ticks.minRotation = 0;
    }
  }
  chart.update("none");
}

const chartData = computed(() => {
  const datasets: any[] = [];
  
  if (props.type === 'temp' || props.type === 'both') {
    datasets.push({
      label: "Temperature (°C)",
      data: props.seriesT,
      borderColor: "#22c55e",
      backgroundColor: "rgba(34, 197, 94, 0.1)",
      yAxisID: "y",
      tension: 0.3,
      pointRadius: 0,
      borderWidth: 2,
    });
  }

  if (props.type === 'hum' || props.type === 'both') {
    datasets.push({
      label: "Humidity (%)",
      data: props.seriesH,
      borderColor: "#8aeaff",
      backgroundColor: "rgba(138, 234, 255, 0.1)",
      yAxisID: props.type === 'both' ? "y1" : "y",
      tension: 0.3,
      pointRadius: 0,
      borderWidth: 2,
    });
  }

  // Helper to get time range for straight lines
  const getTimeRange = () => {
    const dataSources = [];
    if (props.type === 'temp' || props.type === 'both') dataSources.push(...props.seriesT);
    if (props.type === 'hum' || props.type === 'both') dataSources.push(...props.seriesH);
    
    if (dataSources.length === 0) return [];
    
    // Combine all points to find min/max X (time)
    const allX = dataSources.map(p => p.x);
    const minX = Math.min(...allX);
    const maxX = Math.max(...allX);
    
    return [minX, maxX];
  };

  const timeRange = getTimeRange();
  const hasData = timeRange.length === 2;

  if (hasData) {
    // Temperature Thresholds
    if (props.type === 'temp' || props.type === 'both') {
        if (props.tempMin != null) {
          datasets.push({
            label: "Temp Min Limit",
            data: [{ x: timeRange[0], y: props.tempMin }, { x: timeRange[1], y: props.tempMin }],
            borderColor: "rgba(34, 197, 94, 0.4)",
            borderDash: [5, 5],
            borderWidth: 1.5,
            pointRadius: 0,
            fill: false,
            yAxisID: "y"
          });
        }
        if (props.tempMax != null) {
          datasets.push({
            label: "Temp Max Limit",
            data: [{ x: timeRange[0], y: props.tempMax }, { x: timeRange[1], y: props.tempMax }],
            borderColor: "rgba(239, 68, 68, 0.5)",
            borderDash: [5, 5],
            borderWidth: 1.5,
            pointRadius: 0,
            fill: false,
            yAxisID: "y"
          });
        }
    }

    // Humidity Thresholds
    if (props.type === 'hum' || props.type === 'both') {
        const targetY = props.type === 'both' ? "y1" : "y";
        if (props.humMin != null) {
          datasets.push({
            label: "Hum Min Limit",
            data: [{ x: timeRange[0], y: props.humMin }, { x: timeRange[1], y: props.humMin }],
            borderColor: "rgba(138, 234, 255, 0.4)",
            borderDash: [5, 5],
            borderWidth: 1.5,
            pointRadius: 0,
            fill: false,
            yAxisID: targetY
          });
        }
        if (props.humMax != null) {
          datasets.push({
            label: "Hum Max Limit",
            data: [{ x: timeRange[0], y: props.humMax }, { x: timeRange[1], y: props.humMax }],
            borderColor: "rgba(59, 130, 246, 0.5)",
            borderDash: [5, 5],
            borderWidth: 1.5,
            pointRadius: 0,
            fill: false,
            yAxisID: targetY
          });
        }
    }
  }

  return { datasets };
});

const intervalConfig = computed(() => {
  let unit = "minute" as const;
  let stepSize = 30;

  if (props.currentRange === "1h") {
    stepSize = 1;
  } else {
    stepSize = 30;
  }

  return { unit, stepSize };
});

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "index" as const,
    intersect: false,
  },
  scales: {
    x: {
      ...(chartTimeConfig.scales.x as any),
      bounds: 'ticks',
      time: {
        ...chartTimeConfig.scales.x.time,
        unit: intervalConfig.value.unit,
        stepSize: intervalConfig.value.stepSize,
        round: 'minute'
      },
      ticks: { 
        ...chartTimeConfig.scales.x.ticks,
        autoSkip: props.currentRange === '1h' || props.currentRange === '24h' ? false : true,
        maxRotation: 45,
        minRotation: 45,
        maxTicksLimit: 100,
        callback: function(value: any, index: any, ticks: any) {
          const date = new Date(value);
          if (props.currentRange !== '1h' && date.getUTCMinutes() % 30 !== 0) return null;
          return chartTimeConfig.scales.x.ticks.callback(value, index, ticks);
        }
      },
      adapters: { ...chartTimeConfig.scales.x.adapters },
    },
    y: {
      type: "linear" as const,
      display: true,
      position: "left" as const,
      grid: {
        color: "rgba(255, 255, 255, 0.05)",
      },
      ticks: { 
        color: props.type === 'hum' ? '#8aeaff' : "#9fb0c2" 
      },
    },
    y1: {
      type: "linear" as const,
      display: props.type === 'both',
      position: "right" as const,
      grid: {
        drawOnChartArea: false,
      },
      ticks: { color: "#8aeaff" },
    },
  },
  plugins: {
    legend: {
      labels: { color: "#c9d3de" },
    },
    tooltip: chartTimeConfig.plugins.tooltip,
    zoom: {
      zoom: {
        wheel: { 
          enabled: true,
          modifierKey: 'alt' as const
        },
        pinch: { enabled: true },
        mode: "x" as const,
        onZoom: ({ chart }: { chart: any }) => updateChartIntervals(chart),
      },
      pan: {
        enabled: true,
        mode: "x" as const,
        onPan: ({ chart }: { chart: any }) => updateChartIntervals(chart),
      },
    },
  },
}));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", null, [
    _createElementVNode("header", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("h3", null, _toDisplayString(__props.title), 1),
        (__props.currentTemp !== undefined || __props.currentHum !== undefined)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              ((__props.type === 'temp' || __props.type === 'both') && __props.currentTemp !== undefined)
                ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(formatTemp(__props.currentTemp)), 1))
                : _createCommentVNode("", true),
              ((__props.type === 'hum' || __props.type === 'both') && __props.currentHum !== undefined)
                ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(formatHum(__props.currentHum)), 1))
                : _createCommentVNode("", true),
              (__props.lastUpdateAgo)
                ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(__props.lastUpdateAgo), 1))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_7, [
        _renderSlot(_ctx.$slots, "header-actions"),
        _cache[6] || (_cache[6] = _createElementVNode("div", { class: "divider" }, null, -1)),
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(['24h', '7d', '30d'], (r) => {
          return _createElementVNode("button", {
            key: r,
            class: _normalizeClass(["time-btn", { active: __props.currentRange === r }]),
            onClick: ($event: any) => (_ctx.$emit('update:range', r))
          }, _toDisplayString(r), 11, _hoisted_8)
        }), 64)),
        _createElementVNode("button", {
          class: "time-btn",
          title: "Zoom Out",
          onClick: zoomOut
        }, " − "),
        _createElementVNode("button", {
          class: "time-btn",
          title: "Zoom In",
          onClick: zoomIn
        }, " + "),
        _createElementVNode("button", {
          class: "time-btn",
          title: "Reset Zoom",
          onClick: resetZoom
        }, [...(_cache[5] || (_cache[5] = [
          _createElementVNode("svg", {
            xmlns: "http://www.w3.org/2000/svg",
            width: "14",
            height: "14",
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            "stroke-width": "2",
            "stroke-linecap": "round",
            "stroke-linejoin": "round"
          }, [
            _createElementVNode("path", { d: "M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" })
          ], -1)
        ]))]),
        _createElementVNode("button", {
          class: "time-btn",
          title: "Threshold Settings",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('config')))
        }, [
          _createVNode(_unref(FontAwesomeIcon), { icon: _unref(faSliders) }, null, 8, ["icon"])
        ]),
        _createElementVNode("div", _hoisted_9, [
          ((__props.type === 'temp' || __props.type === 'both') && (__props.tempMin !== undefined || __props.tempMax !== undefined))
            ? (_openBlock(), _createElementBlock("span", _hoisted_10, [
                (__props.tempMin !== undefined)
                  ? (_openBlock(), _createElementBlock("span", {
                      key: 0,
                      class: "temp-limit-val min",
                      title: "Min Temp Threshold",
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('config')))
                    }, "MIN: " + _toDisplayString(__props.tempMin), 1))
                  : _createCommentVNode("", true),
                (__props.tempMax !== undefined)
                  ? (_openBlock(), _createElementBlock("span", {
                      key: 1,
                      class: "temp-limit-val max",
                      title: "Max Temp Threshold",
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('config')))
                    }, "MAX: " + _toDisplayString(__props.tempMax), 1))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          ((__props.type === 'hum' || __props.type === 'both') && (__props.humMin !== undefined || __props.humMax !== undefined))
            ? (_openBlock(), _createElementBlock("span", _hoisted_11, [
                (__props.humMin !== undefined)
                  ? (_openBlock(), _createElementBlock("span", {
                      key: 0,
                      class: "hum-limit-val min",
                      title: "Min Hum Threshold",
                      onClick: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('config')))
                    }, "MIN: " + _toDisplayString(__props.humMin), 1))
                  : _createCommentVNode("", true),
                (__props.humMax !== undefined)
                  ? (_openBlock(), _createElementBlock("span", {
                      key: 1,
                      class: "hum-limit-val max",
                      title: "Max Hum Threshold",
                      onClick: _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('config')))
                    }, "MAX: " + _toDisplayString(__props.humMax), 1))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ])
      ])
    ]),
    _createElementVNode("div", {
      class: "card-body chart-body chart-container-rel",
      onWheel: handleWheel
    }, [
      (__props.loading)
        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [...(_cache[7] || (_cache[7] = [
            _createElementVNode("span", { class: "blinking-text" }, "Loading...", -1)
          ]))]))
        : _createCommentVNode("", true),
      _createVNode(_unref(Line), {
        ref_key: "chartRef",
        ref: chartRef,
        data: chartData.value,
        options: chartOptions.value
      }, null, 8, ["data", "options"]),
      _createElementVNode("div", {
        class: _normalizeClass(["zoom-hint-overlay", { show: showZoomHint.value }])
      }, [...(_cache[8] || (_cache[8] = [
        _createElementVNode("div", { class: "zoom-hint-content" }, [
          _createTextVNode(" Usa "),
          _createElementVNode("kbd", null, "Alt"),
          _createTextVNode(" + rotella per zoomare ")
        ], -1)
      ]))], 2)
    ], 32)
  ]))
}
}

})